import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { api, isVideoPath } from "@/lib/api";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { BeforeAfter } from "./BeforeAfterSlider";
import { FullscreenViewer } from "./FullscreenViewer";
import { galleryShare } from "@/lib/share";
import { WorkMedia } from "./gallery/WorkMedia";

export { BeforeAfter } from "./BeforeAfterSlider";

// Tap-to-fullscreen wrapper: the slider (tap it, or the expand button) opens the fullscreen
// viewer; pass group/groupIndex to swipe through every pair on the page. This lives OUTSIDE
// BeforeAfter on purpose — a component that renders itself makes the dev-server visual-edits
// babel plugin recurse forever ("Maximum call stack size exceeded"). No JSX cycles.
export const BeforeAfterZoomable = ({ beforeSrc, afterSrc, caption, testid, onError, group, groupIndex = 0, share }) => {
  const [open, setOpen] = useState(null);
  const list = group?.length ? group : [{ beforeSrc, afterSrc, caption, share }];
  const start = group?.length ? groupIndex : 0;
  return (
    <div className="relative">
      <BeforeAfter beforeSrc={beforeSrc} afterSrc={afterSrc} caption={caption} testid={testid} onError={onError}
        onOpen={() => setOpen(start)} />
      <button
        type="button"
        data-testid={`${testid}-expand`}
        aria-label="View full screen"
        onClick={() => setOpen(start)}
        className="absolute bottom-2 right-2 z-10 h-11 w-11 flex items-center justify-center bg-black/60 hover:bg-terracotta text-white rounded-full transition-colors"
      >
        <Maximize2 className="h-5 w-5" />
      </button>
      {open !== null && (
        <FullscreenViewer
          items={list.map((p) => ({ kind: "pair", beforeSrc: p.beforeSrc, afterSrc: p.afterSrc, caption: p.caption, share: p.share, testid: `${testid}-zoom` }))}
          index={open}
          onNav={(d) => setOpen((v) => Math.min(list.length - 1, Math.max(0, v + d)))}
          onClose={() => setOpen(null)}
          ids={{
            root: `${testid}-fullscreen`, close: `${testid}-fullscreen-close`, prev: `${testid}-fullscreen-prev`,
            next: `${testid}-fullscreen-next`, counter: `${testid}-fullscreen-counter`, caption: `${testid}-fullscreen-caption`,
          }}
        />
      )}
    </div>
  );
};

// A Work Gallery item as a fullscreen viewer slide (pairs keep their slider; videos get controls).
export const lightboxItem = (item) => {
  const src = `/api/gallery/image/${item.image_path}`;
  if (item.after_path) {
    return { kind: "pair", beforeSrc: src, afterSrc: `/api/gallery/image/${item.after_path}`, caption: item.caption,
      share: galleryShare(item.id), testid: "work-gallery-lightbox-slider" };
  }
  if (isVideoPath(item.image_path)) {
    return { kind: "video", src, poster: item.poster_path ? `/api/gallery/image/${item.poster_path}` : undefined,
      caption: item.caption, testid: "work-gallery-lightbox-video" };
  }
  return { kind: "image", src, alt: item.caption || "Recent job photo", caption: item.caption, testid: "work-gallery-lightbox-image" };
};

// Phones load the strip in batches: 6 first, then 6 more each time the swipe nears the end.
const MOBILE_BATCH = 6;

// Public work strip — real job photos Bryan uploads from the admin dashboard.
// On a town page (city="Poulsbo") it shows the photos whose caption carries "Poulsbo, WA";
// with none tagged yet it falls back to the latest photos as "Recent work nearby".
export const WorkGallery = ({ city } = {}) => {
  const [items, setItems] = useState(null);
  const [viewIdx, setViewIdx] = useState(null);
  const [broken, setBroken] = useState(() => new Set()); // ids whose file is gone from storage
  const stripRef = useRef(null);
  const isMobile = useIsMobile();
  const [shown, setShown] = useState(MOBILE_BATCH);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, []);

  const scrollByCard = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    const card = el.querySelector("[data-testid^='work-gallery-item-']");
    const w = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  // a card whose file 404s drops out of the strip instead of sitting there white forever
  const markBroken = (id) => setBroken((s) => (s.has(id) ? s : new Set(s).add(id)));
  const visible = (items || []).filter((it) => !broken.has(it.id));
  const tag = city ? `${city}, WA`.toLowerCase() : "";
  const tagged = city ? visible.filter((it) => (it.caption || "").toLowerCase().includes(tag)) : [];
  const local = tagged.length > 0;
  const itemsForView = !city ? visible : local ? tagged : visible.slice(0, 8);
  const stripItems = isMobile ? itemsForView.slice(0, shown) : itemsForView;
  const hasMore = stripItems.length < itemsForView.length;

  // auto-load the next batch once the last loaded card is within a screen-width of view
  useEffect(() => {
    const strip = stripRef.current;
    const last = strip?.lastElementChild;
    if (!hasMore || !last) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setShown((n) => n + MOBILE_BATCH);
    }, { root: strip, rootMargin: "0px 25% 0px 0px" });
    io.observe(last);
    return () => io.disconnect();
  }, [hasMore, stripItems.length]);

  if (!items || visible.length === 0) return null;
  const copy = !city
    ? { over: "Recent work", title: "Fresh off the van", sub: "Real jobs from around North Kitsap — shot on site, not stock." }
    : local
      ? { over: `Recent work in ${city}`, title: `Done right here in ${city}`, sub: `Real jobs in ${city} — shot on site, not stock.` }
      : { over: "Recent work nearby", title: "Fresh off the van", sub: "Real jobs from around North Kitsap — shot on site, not stock." };

  const navBtnCls =
    "shrink-0 h-12 w-12 flex items-center justify-center border border-navy/30 text-navy hover:bg-navy hover:text-white rounded-full transition-colors";

  return (
    <section id="work" data-testid={city ? "city-work-gallery" : "work-gallery"} data-local={city ? String(local) : undefined} className="py-16 sm:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between gap-4 mb-8"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-terracotta mb-2 flex items-center gap-2" data-testid="work-gallery-overline">
              <Camera className="h-3.5 w-3.5" /> {copy.over}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-navy tracking-tight" data-testid="work-gallery-title">
              {copy.title}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <p className="hidden sm:block text-sm text-muted-foreground max-w-xs text-right">
              {copy.sub}
            </p>
            <button type="button" data-testid="work-gallery-prev" aria-label="Scroll work photos back"
              onClick={() => scrollByCard(-1)} className={navBtnCls}>
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" data-testid="work-gallery-next" aria-label="Scroll work photos forward"
              onClick={() => scrollByCard(1)} className={navBtnCls}>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>

        <div ref={stripRef} className={`grid ${stripItems.length > 3 ? "grid-rows-2" : "grid-rows-1"} grid-flow-col auto-cols-[82%] sm:auto-cols-[46%] lg:auto-cols-[31%] gap-4 overflow-x-auto pb-4 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-5 px-5 sm:mx-0 sm:px-0`} data-testid="work-gallery-strip" data-shown={stripItems.length} data-total={itemsForView.length}>
          {stripItems.map((item, i) => (
            <motion.figure
              key={item.id}
              data-testid={`work-gallery-item-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: Math.min(i, 4) * 0.08 }}
              className="w-full snap-center group"
            >
              <div className="relative overflow-hidden border border-border bg-card">
                <WorkMedia item={item} prefix="work-gallery" i={i} onOpen={() => setViewIdx(i)} onBroken={() => markBroken(item.id)} />
              </div>
              {item.caption && (
                <figcaption className="pt-2.5 text-sm text-muted-foreground">{item.caption}</figcaption>
              )}
            </motion.figure>
          ))}
        </div>
        <Link to="/gallery" data-testid="work-gallery-see-all"
          className="group/see mt-4 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-terracotta transition-colors">
          See all my work
          <ArrowRight className="h-4 w-4 transition-transform group-hover/see:translate-x-1" />
        </Link>
      </div>
      {viewIdx !== null && (
        <FullscreenViewer
          items={itemsForView.map(lightboxItem)}
          index={viewIdx}
          onClose={() => setViewIdx(null)}
          onNav={(dir) => setViewIdx((v) => Math.min(itemsForView.length - 1, Math.max(0, v + dir)))}
          ids={{
            root: "work-gallery-lightbox", close: "work-gallery-lightbox-close", prev: "work-gallery-lightbox-prev",
            next: "work-gallery-lightbox-next", counter: "work-gallery-lightbox-counter", caption: "work-gallery-lightbox-caption",
          }}
        />
      )}
    </section>
  );
};
